export interface PasswordValidationResult {
    isValid: boolean;
    errors: string[];
}

export interface PasswordRequirements {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumber: boolean;
    requireSpecialChar: boolean;
}

const DEFAULT_REQUIREMENTS: PasswordRequirements = {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumber: true,
    requireSpecialChar: true,
};

export class PasswordValidator {
    private requirements: PasswordRequirements;

    constructor(requirements?: Partial<PasswordRequirements>) {
        this.requirements = { ...DEFAULT_REQUIREMENTS, ...requirements };
    }

    /**
     * Validates a password against the configured requirements
     * @param password - The password to validate
     * @returns PasswordValidationResult containing validation status and error messages
     */
    public validate(password: string): PasswordValidationResult {
        const errors: string[] = [];

        if (!password) {
            return {
                isValid: false,
                errors: ['Password cannot be empty.'],
            };
        }

        // Check minimum length
        if (password.length < this.requirements.minLength) {
            errors.push(`Password must be at least ${this.requirements.minLength} characters long.`);
        }

        // Check for uppercase letter
        if (this.requirements.requireUppercase && !/[A-Z]/.test(password)) {
            errors.push('Password must contain at least one uppercase letter.');
        }

        // Check for lowercase letter
        if (this.requirements.requireLowercase && !/[a-z]/.test(password)) {
            errors.push('Password must contain at least one lowercase letter.');
        }

        // Check for number
        if (this.requirements.requireNumber && !/[0-9]/.test(password)) {
            errors.push('Password must contain at least one number.');
        }

        // Check for special character
        if (this.requirements.requireSpecialChar && !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
            errors.push('Password must contain at least one special character (e.g., !@#$%^&*).');
        }

        return {
            isValid: errors.length === 0,
            errors,
        };
    }

    /**
     * Validates that two passwords match
     * @param password - The password
     * @param confirmPassword - The confirmation password
     * @returns PasswordValidationResult
     */
    public validateMatch(password: string, confirmPassword: string): PasswordValidationResult {
        if (password !== confirmPassword) {
            return {
                isValid: false,
                errors: ['Passwords do not match. Please ensure both password fields are identical.'],
            };
        }

        return {
            isValid: true,
            errors: [],
        };
    }

    /**
     * Performs complete password validation including strength and match check
     * @param password - The password
     * @param confirmPassword - The confirmation password
     * @returns PasswordValidationResult with all validation errors
     */
    public validatePasswordWithConfirmation(password: string, confirmPassword: string): PasswordValidationResult {
        const strengthValidation = this.validate(password);
        const matchValidation = this.validateMatch(password, confirmPassword);

        const allErrors = [...strengthValidation.errors, ...matchValidation.errors];

        return {
            isValid: allErrors.length === 0,
            errors: allErrors,
        };
    }

    /**
     * Gets a human-readable description of the password requirements
     * @returns Array of requirement descriptions
     */
    public getRequirementsDescription(): string[] {
        const descriptions: string[] = [];

        descriptions.push(`At least ${this.requirements.minLength} characters`);

        if (this.requirements.requireUppercase) {
            descriptions.push('At least one uppercase letter (A-Z)');
        }

        if (this.requirements.requireLowercase) {
            descriptions.push('At least one lowercase letter (a-z)');
        }

        if (this.requirements.requireNumber) {
            descriptions.push('At least one number (0-9)');
        }

        if (this.requirements.requireSpecialChar) {
            descriptions.push('At least one special character (!@#$%^&*...)');
        }

        return descriptions;
    }
}

// Export a default instance with standard requirements
export const defaultPasswordValidator = new PasswordValidator();
